import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import ProductCard from "./ProductCard";
import { ratingFor } from "../lib/ratings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isSN = (title) =>
  /shantanu|shantnu|nikhil|\bs&n\b|\bs and n\b/i.test(title || "");

const toCard = (p) => ({
  id: p.handle || p.id,
  name: p.title,
  category: "shantanu-nikhil",
  price: Math.round(p.price || 0),
  originalPrice: p.compareAtPrice ? Math.round(p.compareAtPrice) : null,
  badge: "S&N Exclusive",
  rating: ratingFor(p.handle || p.id),
  image: p.image || null,
  variantId: p.variantId,
  availableForSale: p.availableForSale !== false,
  quantityAvailable: typeof p.quantityAvailable === "number" ? p.quantityAvailable : null,
  currentlyNotInStock: !!p.currentlyNotInStock,
});

const SNCollection = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/shop/products`);
        const data = await res.json();
        if (cancelled || !data.configured) return;
        const list = (data.products || []).filter((p) => isSN(p.title)).map(toCard);
        setItems(list);
      } catch (e) {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="sn-collection"
      className="relative z-10 scroll-mt-28 overflow-hidden bg-[#080B14] py-24 md:py-32"
      data-testid="sn-collection-section"
    >
      {/* Ambient gold blooms */}
      <div className="pointer-events-none absolute -left-40 top-24 h-[520px] w-[520px] bloom-gold opacity-30" />
      <div className="pointer-events-none absolute -right-24 bottom-16 h-[420px] w-[420px] bloom-gold opacity-25" />

      {/* Fine gold border top/bottom */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-4xl text-center"
        >
          <div
            className="mx-auto inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-1.5 backdrop-blur"
            data-testid="sn-exclusive-pill"
          >
            <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.8} />
            <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-[#F3E5AB]">
              Exclusive Collection
            </span>
          </div>

          <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-[#F3EEE3] md:text-6xl">
            Shantnu Nikhil <span className="italic text-[#D4AF37]">Luxury Collection</span>
          </h2>

          <div className="mx-auto mt-5 h-px w-24 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

          <p className="mx-auto mt-6 max-w-2xl font-sans text-base leading-relaxed text-[#A9B0C2] md:text-lg">
            An exclusive festive edit crafted with India&apos;s premier couture house — every piece
            curated for discerning taste, delivered in 48 hours.
          </p>
        </motion.div>

        {/* Product grid — showcase ALL */}
        <div
          className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="sn-collection-grid"
        >
          {items.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>

        {items.length === 0 && (
          <div className="mt-16 text-center font-sans text-sm text-[#8B93A7]">
            Curating the exclusive edit… please refresh in a moment.
          </div>
        )}

        {/* Footer link */}
        {items.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-14 flex flex-col items-center gap-3 text-center"
          >
            <p className="font-sans text-xs uppercase tracking-[0.28em] text-[#8B93A7]">
              {items.length} pieces · Express 2-Day dispatch
            </p>
            <a
              href="https://www.consortiumgifts.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sn-view-parent-range"
              className="mt-2 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-transparent px-6 py-3 font-sans text-sm tracking-wide text-[#F3E5AB] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0F19] hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]"
            >
              Explore the full Shantanu & Nikhil archive
              <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default SNCollection;
